import axios from "axios";
import { BASE_API_URL } from "../../utils/consts";
import { isLocationActive } from "../../utils/helpers/locationVisibility";
import {
  ClientInvestment,
  ClientInvestmentPayment,
  CreateInvestmentPayload,
  ClientLocation,
} from "./types";

const withAuthHeaders = (token?: string) => {
  if (!token) return {};
  return {
    headers: {
      "x-access-token": token,
    },
  };
};

export const fetchInvestments = async (
  clientId: string,
  token?: string
): Promise<ClientInvestment[]> => {
  const { data } = await axios.get(`${BASE_API_URL}/investments-v2`, {
    params: { clientId },
    ...withAuthHeaders(token),
  });
  return data;
};

export const fetchInvestmentDetails = async (
  investmentId: string,
  token?: string
): Promise<ClientInvestment> => {
  const { data } = await axios.get(
    `${BASE_API_URL}/investments-v2/${investmentId}`,
    withAuthHeaders(token)
  );
  return data;
};

export const createInvestment = async (
  payload: CreateInvestmentPayload,
  token?: string
): Promise<ClientInvestment> => {
  const { data } = await axios.post(
    `${BASE_API_URL}/investments-v2`,
    payload,
    withAuthHeaders(token)
  );
  return data;
};

export const updateInvestment = async (
  investmentId: string,
  payload: Partial<CreateInvestmentPayload> & { status?: string },
  token?: string
): Promise<ClientInvestment> => {
  const { data } = await axios.put(
    `${BASE_API_URL}/investments-v2/${investmentId}`,
    payload,
    withAuthHeaders(token)
  );
  return data;
};

export const addManualPayment = async (
  investmentId: string,
  amount: number,
  token?: string,
  note?: string,
  paymentDate?: string
): Promise<ClientInvestment> => {
  const { data } = await axios.post(
    `${BASE_API_URL}/investments-v2/${investmentId}/manual-payments`,
    { amount, note, paymentDate },
    withAuthHeaders(token)
  );
  return data;
};

export const updatePayment = async (
  investmentId: string,
  paymentId: string,
  payload: Partial<ClientInvestmentPayment>,
  token?: string
): Promise<ClientInvestment> => {
  const { data } = await axios.patch(
    `${BASE_API_URL}/investments-v2/${investmentId}/payments/${paymentId}`,
    payload,
    withAuthHeaders(token)
  );
  return data;
};

export const deletePayment = async (
  investmentId: string,
  paymentId: string,
  token?: string
): Promise<ClientInvestment> => {
  const { data } = await axios.delete(
    `${BASE_API_URL}/investments-v2/${investmentId}/payments/${paymentId}`,
    withAuthHeaders(token)
  );
  return data;
};

export const markInstallmentPaid = async (
  investmentId: string,
  token?: string
): Promise<ClientInvestment> => {
  const { data } = await axios.post(
    `${BASE_API_URL}/investments-v2/${investmentId}/mark-installment`,
    {},
    withAuthHeaders(token)
  );
  return data;
};

export const deleteInvestment = async (
  investmentId: string,
  token?: string
) => {
  await axios.delete(
    `${BASE_API_URL}/investments-v2/${investmentId}`,
    withAuthHeaders(token)
  );
};

export const fetchLocations = async (
  clientId: string,
  token?: string
): Promise<ClientLocation[]> => {
  const { data } = await axios.get(`${BASE_API_URL}/${clientId}/locations`, {
    ...withAuthHeaders(token),
  });
  return (data || []).filter(isLocationActive);
};

export type { ClientInvestmentPayment };
